/**
 * Locations hooks. The crew's "departing from" origin options (VCS-403) are the
 * VariSpace warehouse / home-base sites with coordinates, used as the ETA origin
 * when GPS is unavailable.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Location,
  LocationCreateInput,
  LocationKind,
  LocationUpdateInput,
} from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';

export interface OriginLocation {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  line1: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
}

type ListResponse = { data: OriginLocation[] };

export function useOriginLocations() {
  return useQuery<ListResponse>({
    queryKey: ['origin-locations'],
    queryFn: () => apiCall<ListResponse>('/origin-locations'),
    staleTime: 5 * 60_000,
  });
}

// ---- Registry locations (warehouse management CRUD — supervisor/admin) ----

type LocationListResponse = { data: Location[] };

/** Full registry locations, optionally filtered by kind (e.g. 'vari_warehouse').
 *  The single source the intake dropdown + AI grounding + trip calculator read. */
export function useLocations(kind?: LocationKind) {
  return useQuery<LocationListResponse>({
    queryKey: ['locations', kind ?? 'all'],
    queryFn: () =>
      apiCall<LocationListResponse>(`/locations${kind ? `?kind=${kind}` : ''}`),
    staleTime: 5 * 60_000,
  });
}

function invalidateLocations(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['locations'] });
  qc.invalidateQueries({ queryKey: ['origin-locations'] });
}

export function useCreateLocation() {
  const qc = useQueryClient();
  return useMutation<Location, Error, LocationCreateInput>({
    mutationFn: (body) => apiCall<Location>('/locations', { method: 'POST', body }),
    onSuccess: () => invalidateLocations(qc),
  });
}

export function useUpdateLocation() {
  const qc = useQueryClient();
  return useMutation<Location, Error, { id: string; body: LocationUpdateInput }>({
    mutationFn: ({ id, body }) =>
      apiCall<Location>(`/locations/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateLocations(qc),
  });
}

/** Soft-delete (deactivate) — drops it from every list everywhere. */
export function useDeleteLocation() {
  const qc = useQueryClient();
  return useMutation<Location, Error, string>({
    mutationFn: (id) => apiCall<Location>(`/locations/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateLocations(qc),
  });
}
